export const ACTIVITY_POLL_MS = 5000;
const TERMINAL_SETTLE_POLLS = 3;

const TERMINAL_STATUSES = new Set(['Completed', 'Failed', 'Cancelled']);
const STATUS_LABELS = { Pending: 'Queued', Started: 'Starting', InProgress: 'Running', Completed: 'Completed', Failed: 'Failed', Cancelled: 'Cancelled' };

export function isActivityTerminal(status) {
  return TERMINAL_STATUSES.has(status);
}

export function activityStatusLabel(status) {
  return STATUS_LABELS[status] || 'Status not recorded';
}

export function activityProgress(value) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100 ? value : null;
}

/** A session owns its request and timer. Closing/replacing it also invalidates responses when cancellation is too late. */
export function createJobActivityPoller({ fetchActivity, onData, onLoading, onError, onTerminal, onSettling = () => {}, schedule = setTimeout, unschedule = clearTimeout }) {
  let session = null;

  function close() {
    if (!session) return;
    const previous = session;
    session = null;
    unschedule(previous.timer);
    if (previous.controller) previous.controller.abort();
    onSettling(false);
  }

  async function refresh() {
    const current = session;
    if (!current || current.busy) return;
    unschedule(current.timer);
    current.busy = true;
    current.controller = new AbortController();
    onLoading(true);
    try {
      const activity = await fetchActivity(current.id, current.controller.signal);
      if (session !== current) return;
      if (!activity || activity.jobId !== current.id || !Object.prototype.hasOwnProperty.call(STATUS_LABELS, activity.status) || !Array.isArray(activity.events)) {
        throw new Error('Invalid job activity response');
      }
      if (current.terminalObserved && !isActivityTerminal(activity.status)) {
        throw new Error('Job activity status regressed');
      }
      const changedToTerminal = isActivityTerminal(activity.status) && activity.status !== current.status;
      current.status = activity.status;
      current.terminalObserved = isActivityTerminal(activity.status);
      // Job status and the final journal entry use separate storage. Allow a bounded window
      // for that eventual consistency gap, including errors, without polling historical runs.
      current.awaitingFinalActivity = current.terminalObserved && activity.isAvailable === true && activity.stage !== activity.status;
      onData(activity);
      onError(false);
      if (changedToTerminal) onTerminal(activity);
    } catch (err) {
      if (session === current) onError(true);
      if (session === current && err && err.response && [401, 403].includes(err.response.status)) {
        onLoading(false);
        close();
      }
    } finally {
      if (session === current) {
        current.busy = false;
        current.controller = null;
        onLoading(false);
        const settleAgain = current.awaitingFinalActivity && current.settlePollsLeft > 0;
        onSettling(settleAgain);
        if (!isActivityTerminal(current.status) || settleAgain) {
          if (settleAgain) current.settlePollsLeft--;
          current.timer = schedule(refresh, ACTIVITY_POLL_MS);
        }
      }
    }
  }

  function open(id, status) {
    close();
    session = { id, status, busy: false, timer: null, controller: null, terminalObserved: false, awaitingFinalActivity: false, settlePollsLeft: TERMINAL_SETTLE_POLLS };
    return refresh();
  }

  return { open, close, refresh };
}
